import * as React from 'react';

import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {SpacerGroup} from 'shells/spacer/spacer-group';

import type {SmtpPortType} from 'nutshell-graphql-types';
import type {SmtpSettingsUpdateInputFieldInput} from 'nutshell-graphql-types';

import {Hostname, Port, PortType, Username, Password} from './fields';

import styles from './smtp-form.module.css';

const COULD_NOT_OPEN_SOCKET = 'Could not open socket';

export type ExposedSubform = {
    handleSubmit: () => void;
};

type Props = {
    disabled?: boolean;
    errorMessage?: string;
    password: string;
    hostname: string;
    port: string;
    portType: SmtpPortType;
    username: string;
    userEmailId: string;
    onSubmit: (arg1: SmtpSettingsUpdateInputFieldInput) => void;
};

// @ts-expect-error
export const SmtpForm = React.forwardRef<Props, ExposedSubform>((props: Props, ref) => {
    const [hostname, setHostname] = React.useState<string>(props.hostname);
    const [port, setPort] = React.useState<string>(props.port);
    const [portType, setPortType] = React.useState<SmtpPortType>(props.portType);
    const [username, setUsername] = React.useState<string>(props.username);
    const [password, setPassword] = React.useState<string>(props.password);
    const {errorMessage} = props;

    // Expose submission method via the ref
    // @ts-expect-error
    React.useImperativeHandle(ref, () => ({
        handleSubmit() {
            submitForm();
        },
    }));

    function submitForm(e?: React.SyntheticEvent<any> | null) {
        if (e) {
            e.preventDefault();
        }

        return props.onSubmit({
            hostname,
            port,
            ssl: portType,
            username,
            password,
            userEmailId: props.userEmailId,
        });
    }

    return (
        // @ts-expect-error
        <form onSubmit={props.onSubmit} className={styles['smtp-form']}>
            {errorMessage ? (
                <SpacerGroup mb={16}>
                    <FlashBanner key='smtp-error' hasBorder={true} type='error'>
                        <div className={styles['smtp-error-container']}>
                            <div>
                                Could not connect to your SMTP server. Received error message:
                            </div>
                            <strong>{`"${errorMessage}"`}</strong>
                            {errorMessage === COULD_NOT_OPEN_SOCKET
                                ? 'This usually means your credentials are wrong, or that you aren’t authorized to use this email address with your SMTP server.'
                                : undefined}
                        </div>
                    </FlashBanner>
                </SpacerGroup>
            ) : undefined}
            <div className={styles['form-body']}>
                <Hostname value={hostname} onChange={setHostname} />
                <div className={styles['form-row']}>
                    <PortType value={portType} onChange={setPortType} />
                    <Port value={port} onChange={setPort} />
                </div>
                <div className={styles['form-row']}>
                    <Username value={username} onChange={setUsername} label='Username' />
                    <Password value={password} onChange={setPassword} />
                </div>
            </div>
            <button disabled={props.disabled} onClick={submitForm} />
        </form>
    );
});
